import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Crown, Check, Sparkles, XCircle } from "lucide-react";
import { toast } from "sonner";
import { DiscountCodeField } from "@/components/DiscountCodeField";

const PERKS = [
  "Lista alimenti personalizzata da DietFlix",
  "Dieta settimanale su misura",
  "Sostituzioni dinamiche dei pasti",
  "Integratori consigliati",
  "Interpretazione delle analisi del sangue",
  "Lista della spesa con quantità ed export",
];

export default function Subscription() {
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");
  const [provider, setProvider] = useState("stripe");
  const [discount, setDiscount] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/subscription");
    setInfo(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      if (provider === "paypal") {
        const { data } = await api.post("/paypal/subscribe", { lookup_key, origin_url: window.location.origin });
        window.location.href = data.approve_url;
      } else {
        const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin, discount_code: discount?.code });
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy("");
    }
  };

  const cancel = async () => {
    if (!window.confirm("Vuoi disdire il rinnovo? Manterrai l'accesso fino a fine periodo.")) return;
    try {
      await api.post("/subscription/cancel");
      toast.success("Rinnovo disdetto. Accesso attivo fino a fine periodo.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="grid md:grid-cols-2 gap-4">{[0, 1].map((i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}</div>;

  const active = info.is_premium;

  return (
    <div className="space-y-6" data-testid="subscription-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-semibold flex items-center gap-2"><Crown className="h-6 w-6 text-accent" /> Abbonamento</h2>
          <p className="text-muted-foreground text-sm">Sblocca tutte le funzioni intelligenti di DietFlix.</p>
        </div>
        {active && (
          <Badge className="bg-primary/15 text-primary border-primary/30 text-sm px-3 py-1" variant="outline" data-testid="subscription-active-badge">
            {info.comp ? "Accesso incluso" : `Attivo · ${info.plan === "dietflix_yearly" ? "Annuale" : "Mensile"}`}
          </Badge>
        )}
      </div>

      {active ? (
        <Card className="rounded-2xl border-primary/30">
          <CardContent className="p-8 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-primary text-primary-foreground grid place-items-center"><Check className="h-6 w-6" /></div>
              <div>
                <h3 className="font-heading text-xl font-semibold">Premium attivo</h3>
                <p className="text-muted-foreground text-sm">Hai accesso completo a tutte le funzioni.</p>
              </div>
            </div>
            <ul className="grid sm:grid-cols-2 gap-2 pt-2">
              {PERKS.map((p) => <li key={p} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-primary shrink-0" /> {p}</li>)}
            </ul>
            {!info.comp && info.subscription_status === "active" && (
              <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={cancel} data-testid="cancel-subscription-button">
                <XCircle className="h-4 w-4 mr-2" /> Disdici rinnovo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {info.paypal_enabled && (
            <div className="flex items-center gap-2 justify-center" data-testid="provider-toggle">
              <span className="text-sm text-muted-foreground">Paga con:</span>
              <div className="inline-flex rounded-full border p-1 bg-secondary/50">
                <button onClick={() => setProvider("stripe")} data-testid="provider-stripe"
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${provider === "stripe" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                  Carta (Stripe)
                </button>
                <button onClick={() => setProvider("paypal")} data-testid="provider-paypal"
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${provider === "paypal" ? "bg-[#0070ba] text-white" : "text-muted-foreground"}`}>
                  PayPal
                </button>
              </div>
            </div>
          )}
          {provider === "stripe"
            ? <div className="max-w-md mx-auto w-full"><DiscountCodeField discount={discount} onChange={setDiscount} /></div>
            : <p className="text-xs text-muted-foreground text-center" data-testid="paypal-discount-note">I codici sconto sono utilizzabili solo con pagamento con carta.</p>}
          <div className="grid md:grid-cols-2 gap-4">
            {info.plans.map((plan) => {
              const yearly = plan.lookup_key === "dietflix_yearly";
              return (
                <Card key={plan.lookup_key} className={`rounded-2xl relative ${yearly ? "border-accent shadow-lg" : ""}`} data-testid={`plan-card-${plan.lookup_key}`}>
                  {yearly && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full">Risparmi ~25%</span>}
                  <CardContent className="p-8 space-y-5">
                    <div>
                      <div className="text-muted-foreground text-sm">Piano {plan.label}</div>
                      <div className="flex items-end gap-2 mt-1 flex-wrap">
                        {discount && provider === "stripe" && <span className="text-muted-foreground line-through mb-1" data-testid={`plan-original-price-${plan.lookup_key}`}>€{plan.price.toFixed(2).replace(".", ",")}</span>}
                        <span className="font-heading text-4xl font-semibold" data-testid={`plan-price-${plan.lookup_key}`}>€{(discount && provider === "stripe" ? discount.prices[plan.lookup_key] : plan.price).toFixed(2).replace(".", ",")}</span>
                        <span className="text-muted-foreground mb-1">/{plan.interval}</span>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {PERKS.map((p) => <li key={p} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-primary shrink-0" /> {p}</li>)}
                    </ul>
                    <Button className="w-full h-11" variant={yearly ? "default" : "outline"} onClick={() => subscribe(plan.lookup_key)}
                      disabled={!!busy} data-testid={`subscribe-button-${plan.lookup_key}`}>
                      <Sparkles className="h-4 w-4 mr-2" /> {busy === plan.lookup_key ? "Reindirizzamento…" : `Abbonati · ${plan.label}`}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
      {!active && <p className="text-xs text-muted-foreground text-center">Pagamento sicuro con Stripe o PayPal · carta di test Stripe 4242 4242 4242 4242</p>}
    </div>
  );
}
